import { createAsyncThunk } from '@reduxjs/toolkit';

import axiosInstance from 'utils/axios';
import type {
    AdminHospital,
    CreateHospitalPayload,
    FetchAdminHospitalByIdResponse,
    FetchAdminHospitalsParams,
    FetchAdminHospitalsResponse,
    UpdateHospitalPayload
} from '@/types/hospital-admin';
import { getErrorMessage } from 'utils/error-message';

export const fetchAdminHospitals = createAsyncThunk<
    {
        items: AdminHospital[];
        total: number;
        page: number;
        limit: number;
    },
    FetchAdminHospitalsParams | undefined,
    { rejectValue: string }
>('hospitalAdmin/fetchHospitals', async (params, { rejectWithValue }) => {
    try {
        const { data } = await axiosInstance.get<FetchAdminHospitalsResponse>('/admin/hospitals', {
            params: {
                page: params?.page,
                limit: params?.limit,
                search: params?.search || undefined,
                sortBy: params?.sortBy || undefined,
                sortOrder: params?.sortOrder || undefined
            }
        });

        return {
            items: data?.items ?? [],
            total: data?.total ?? 0,
            page: data?.page ?? params?.page ?? 1,
            limit: data?.limit ?? params?.limit ?? 50
        };
    } catch (error: unknown) {
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch hospitals list'));
    }
});

export const fetchAdminHospitalById = createAsyncThunk<AdminHospital, { id: string }, { rejectValue: string }>(
    'hospitalAdmin/fetchHospitalById',
    async ({ id }, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get<FetchAdminHospitalByIdResponse>(`/admin/hospitals/${id}`);
            if (!data?.item) {
                return rejectWithValue('Hospital details not found');
            }
            return data.item;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch hospital details'));
        }
    }
);

export const createHospital = createAsyncThunk<AdminHospital, CreateHospitalPayload, { rejectValue: string }>(
    'hospitalAdmin/createHospital',
    async (payload, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<AdminHospital>('/admin/hospitals', payload);
            return data;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to create hospital'));
        }
    }
);

export const updateHospital = createAsyncThunk<AdminHospital, UpdateHospitalPayload, { rejectValue: string }>(
    'hospitalAdmin/updateHospital',
    async (payload, { rejectWithValue }) => {
        try {
            const { id, ...body } = payload;
            const { data } = await axiosInstance.put<AdminHospital>(`/admin/hospitals/${id}`, body);
            return data;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to update hospital'));
        }
    }
);
